/**
 * @lodex-pro/web-admin/features/tank-catalog/hooks — Task 25.6 (DEC-117).
 *
 * TanStack Query hooks over the EXISTING Task 24.5 standard-tank-catalog API
 * (/v1/platform/tank-catalog) — no new API. GET returns all rows (incl. inactive);
 * deactivate/reactivate reuse PATCH { isActive } (a soft flag that never blocks,
 * unlike the reference-guarded DELETE).
 *
 * DEC-379 (0265): no tank type; a row has a shape, optional dimensions in feet and an
 * optional gross capacity.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import type { TankShape } from '@lodex-pro/shared/tank-strapping';

export interface StandardTank {
  readonly id: string;
  readonly name: string;
  /** numeric columns arrive as strings. */
  readonly capacityBbl: string;
  readonly grossCapacityBbl: string | null;
  readonly bblPerInch: string;
  readonly shape: TankShape;
  readonly diameterFt: string | null;
  readonly lengthFt: string | null;
  readonly widthFt: string | null;
  readonly heightFt: string | null;
  readonly isActive: boolean;
}

export const TANK_CATALOG_KEY = ['platform', 'tank-catalog'] as const;

export function useStandardTanks(): ReturnType<
  typeof useApiQuery<{ standardTanks: readonly StandardTank[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ standardTanks: readonly StandardTank[] }>(
    opts,
    '/v1/platform/tank-catalog',
    { queryKey: TANK_CATALOG_KEY, staleTime: 15_000 },
  );
}

/** The dimension fields a create or edit sends; null clears one. */
export interface TankDimensionsBody {
  diameterFt: number | null;
  lengthFt: number | null;
  widthFt: number | null;
  heightFt: number | null;
  grossCapacityBbl: number | null;
}

export interface CreateTankBody extends TankDimensionsBody {
  name: string;
  capacityBbl: number;
  bblPerInch: number;
  shape: TankShape;
}

export function useCreateTank(
  onDone: () => void,
): ReturnType<typeof useApiMutation<CreateTankBody, { standardTank: StandardTank }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateTankBody, { standardTank: StandardTank }>(
    opts,
    '/v1/platform/tank-catalog',
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TANK_CATALOG_KEY });
        onDone();
      },
    },
  );
}

export interface UpdateTankBody extends Partial<TankDimensionsBody> {
  name?: string;
  capacityBbl?: number;
  bblPerInch?: number;
  shape?: TankShape;
  isActive?: boolean;
}

export function useUpdateTank(
  id: string,
): ReturnType<typeof useApiMutation<UpdateTankBody, { standardTank: StandardTank }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateTankBody, { standardTank: StandardTank }>(
    opts,
    `/v1/platform/tank-catalog/${id}`,
    'PATCH',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: TANK_CATALOG_KEY }) },
  );
}
